import { z } from 'zod';

export const ReviewStatusEnum = z.enum(['PENDING', 'APPROVED', 'REJECTED', 'SPAM']);

// Public & admin customer review submission
export const CreateReviewDtoSchema = z.object({
  productId: z.string().uuid('Valid product ID is required'),
  customerId: z.string().uuid().optional().nullable(),
  customerName: z.string().min(1, 'Name is required'),
  customerEmail: z.string().email().optional().or(z.literal('')).nullable(),
  customerPhone: z.string().optional().or(z.literal('')).nullable(),
  rating: z.number().int().min(1, 'Rating must be between 1 and 5').max(5),
  title: z.string().max(255).optional().nullable(),
  body: z.string().min(1, 'Review body must not be empty'),
  images: z.array(z.string()).default([]),
  videos: z.array(z.string()).default([]),
  audio: z.array(z.string()).default([]),
  status: ReviewStatusEnum.optional().default('APPROVED'),
  verifiedBuyer: z.boolean().optional().default(false),
  adminReply: z.object({
    message: z.string().min(1),
    repliedAt: z.string().datetime().optional()
  }).optional().nullable()
});

// Admin review editing, moderation and reply
export const UpdateReviewStatusDtoSchema = z.object({
  productId: z.string().uuid().optional(),
  customerId: z.string().uuid().optional().nullable(),
  customerName: z.string().min(1).optional(),
  customerEmail: z.string().email().optional().or(z.literal('')).nullable(),
  customerPhone: z.string().optional().or(z.literal('')).nullable(),
  rating: z.number().int().min(1).max(5).optional(),
  title: z.string().max(255).optional().nullable(),
  body: z.string().min(1).optional(),
  images: z.array(z.string()).optional(),
  videos: z.array(z.string()).optional(),
  audio: z.array(z.string()).optional(),
  status: ReviewStatusEnum.optional(),
  verifiedBuyer: z.boolean().optional(),
  adminReply: z.object({
    message: z.string().min(1),
    repliedAt: z.string().datetime().optional()
  }).optional().nullable()
});

export const ReviewQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  cursor: z.string().optional(),
  productId: z.string().uuid().optional(),
  status: ReviewStatusEnum.optional(),
  rating: z.coerce.number().int().min(1).max(5).optional(),
  /** What the merchant typed in the reviews search: the author, the headline or the review itself. */
  search: z.string().trim().max(100).optional()
});

export type CreateReviewDto = z.infer<typeof CreateReviewDtoSchema>;
export type UpdateReviewStatusDto = z.infer<typeof UpdateReviewStatusDtoSchema>;
export type ReviewQuery = z.infer<typeof ReviewQuerySchema>;

// What a shopper may send with a review: their words and rating, never its status, a verified badge or a reply.
export const ShopperReviewDtoSchema = z.object({
  productId: z.string().uuid('Valid product ID is required'),
  customerName: z.string().trim().min(1, 'Name is required').max(120),
  customerEmail: z.string().email().max(254).optional().or(z.literal('')).nullable(),
  customerPhone: z.string().max(30).optional().or(z.literal('')).nullable(),
  rating: z.number().int().min(1, 'Rating must be between 1 and 5').max(5),
  title: z.string().max(255).optional().nullable(),
  body: z.string().trim().min(1, 'Review body must not be empty').max(5000),
  images: z.array(z.string().url().max(500)).max(6).default([])
}).strict();

export type ShopperReviewDto = z.infer<typeof ShopperReviewDtoSchema>;

// What a shopper may ask of a product's reviews: which product, and one page of its approved reviews.
export const PublicReviewQuerySchema = z.object({
  productId: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(10),
  cursor: z.string().max(500).optional()
});

export type PublicReviewQuery = z.infer<typeof PublicReviewQuerySchema>;

/** One approved review as a shopper's page reads it: what was said, by whom, and the product it is about. */
export const PublicReviewSchema = z.object({
  id: z.string(),
  customerName: z.string(),
  rating: z.number(),
  title: z.string().nullable(),
  body: z.string(),
  images: z.array(z.string()).default([]),
  videos: z.array(z.string()).default([]),
  audio: z.array(z.string()).default([]),
  /** The store's reply, shown under the review. */
  adminReply: z.object({ message: z.string(), repliedAt: z.string() }).nullable().default(null),
  verifiedBuyer: z.boolean(),
  productTitle: z.string(),
  productSlug: z.string(),
  productImage: z.string().nullable()
});

export type PublicReview = z.infer<typeof PublicReviewSchema>;

// One moderation action over at most a page of ticked reviews.
export const ReviewBulkActionSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(100),
  action: z.enum(['approve', 'reject', 'spam', 'delete'])
});

export type ReviewBulkActionDto = z.infer<typeof ReviewBulkActionSchema>;
